"use client";
import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import "../../scss/_strategies-valuation.scss";
import heroImage from '../../../public/icons/hero.png';
import insightsdash from '../../../public/icons/insightsdash.png';
import carbondash from '../../../public/icons/carbondash.png';
import researchdash from '../../../public/icons/researchdash.png';
import datadash from '../../../public/icons/datadash.png';
import roundarrow from '../../../public/icons/roundarrow.png';
import { getFeaturedArticle } from '@/services/publicService';

export default function StrategiesValuation() {
  const [openFaq, setOpenFaq] = useState(0);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const strategies = [
    {
      title: "Featured Strategies",
      description: "",
      isHeader: true
    },
    {
      title: "Growth Investing in Oil & Gas",
      description: "Focus on companies or assets with high growth potential driven by innovation, new discoveries, or market expansion."
    },
    {
      title: "Value Investing",
      description: "Identify undervalued stocks or assets with solid fundamentals and long-term upside."
    },
    {
      title: "Impact or ESG Investing",
      description: "Invest in projects that align with environmental and social responsibility, including cleaner technologies and reduced emissions."
    },
    {
      title: "Private Equity in Oil & Gas",
      description: "Explore how institutional investors and private capital fuel exploration, drilling, and production through direct investments."
    },
    {
      title: "Oil and Gas Limited Partnerships",
      description: "Learn about limited partnerships (LPs), their structure, investor roles, and revenue-sharing mechanisms."
    }
  ];

  const valuationTopics = [
    {
      title: "Net Present Value (NPV) in Oil & Gas Projects",
      description: "A core valuation method that discounts future cash flows to assess profitability."
    },
    {
      title: "Internal Rate of Return (IRR) Calculations",
      description: "Determine expected annualized returns on oil and gas investments."
    },
    {
      title: "Production Decline Curves and Valuation Impact",
      description: "Learn how decline analysis affects project value over time."
    },
    {
      title: "Risk Factors in Oil & Gas Valuations",
      description: "Understand how commodity price volatility, geopolitical factors, and environmental risks influence valuations."
    },
    {
      title: "Understanding Reserve Reports and Their Role in Valuation",
      description: "Analyze Proved, Probable, and Possible (3P) reserves and their relevance in investment decisions."
    }
  ];

  const investorTypes = [
    {
      title: "Accredited Investors",
      description: "Learn how SEC rules, income thresholds, and net worth requirements determine access to certain investments."
    },
    {
      title: "Qualified Institutional Buyers (QIBs)",
      description: "See how large financial entities qualify and play a central role in energy markets."
    },
    {
      title: "Private Equity and Institutional Investors",
      description: "Understand how major players fund projects, manage large-scale risk, and leverage capital."
    },
    {
      title: "Working Interests vs. Partnerships",
      description: "Compare active ownership in drilling operations with more passive partnership models."
    }
  ];

  const taxAdvantages = [
    {
      title: "Intangible Drilling Costs (IDC)",
      icon: insightsdash,
      description: "These non-physical expenses like labor and site preparation are often 100% deductible in the year incurred, providing significant upfront tax advantages for investors."
    },
    {
      title: "Tangible Drilling Costs",
      icon: carbondash,
      description: "Understand how equipment and other capital costs are depreciated over time, supporting long-term tax planning."
    },
    {
      title: "Depletion Allowance in Oil & Gas",
      icon: researchdash,
      description: "As resources are extracted, understand how the IRS allows a percentage-based tax deduction to offset diminishing reserves."
    },
    {
      title: "State and Severance Taxes",
      icon: datadash,
      description: "Explore how Texas taxes oil and gas production and how other state-specific rules may affect investor returns."
    }
  ];

  // Static fallback shown until featured articles load (or if none are featured)
  const fallbackInsights = [
    {
      title: "Why NPV Matters in Energy Investments",
      description: "Evaluate project profitability using Net Present Value.",
      image: "/icons/hero.png",
      slug: ""
    },
    {
      title: "Growth vs. Value Investing in Oil & Gas",
      description: "Compare two key strategies for maximizing returns.",
      image: "/icons/hero.png",
      slug: ""
    },
    {
      title: "Accredited vs. Institutional Investors: Who Can Invest?",
      description: "Know the difference between investor types & access.",
      image: "/icons/hero.png",
      slug: ""
    }
  ];

  const [featuredInsights, setFeaturedInsights] = useState(fallbackInsights);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const response = await getFeaturedArticle();
        const articles = Array.isArray(response?.data) ? response.data : [];
        if (isMounted && articles.length > 0) {
          setFeaturedInsights(
            articles.slice(0, 3).map((article) => ({
              title: article.title,
              description: article.summary || article.excerpt || article.meta_description || '',
              image: article.image?.url || article.image || '/icons/hero.png',
              slug: article.slug || ''
            }))
          );
        }
      } catch (error) {
        console.error('Error fetching featured articles:', error);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  const faqs = [
    {
      question: "What is intangible drilling costs (IDC) in oil and gas?",
      answer: "Intangible drilling costs (IDC) are expenses for non-physical assets such as labor and fuel that are used during the production and preparation of the drilling process."
    },
    {
      question: "How is the oil and gas depletion allowance calculated?",
      answer: "The oil and gas depletion allowance is calculated by applying a percentage (usually 15%) to the value of the resources being extracted to account for the decrease in the resource's value."
    },
    {
      question: "Who qualifies as an oil and gas accredited investor?",
      answer: "Accredited investors are those who are considered financially sophisticated and capable of managing the risks associated with oil and gas investment. Some common financial criteria include net worth above $1 million or earnings of more than $200K per year."
    },
    {
      question: "How do private equity firms invest in oil and gas?",
      answer: "Private equity firms invest in oil and gas by providing capital to exploration, drilling, and production companies, often in exchange for equity or a share of future profits."
    },
    {
      question: "What taxes do oil and gas investors face in Texas?",
      answer: "Oil and gas investors face a severance tax of 4.6% on oil and 7.5% on gas production. Investors may also benefit from some exemptions from this state tax."
    }
  ];

  return (
    <div className="strategies-valuation-page-sv">
      {/* Hero Section */}
      <section className="hero-section-sv">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="hero-content-sv">
                <span className="hero-label-sv">STRATEGIES & VALUATION</span>
                <h1 className="hero-title-sv">Master Oil & Gas Investment Strategies and Valuation Techniques</h1>
                <p className="hero-description-sv">
                  Oil and gas investing requires more than capital, but also a strategic mindset. By learning how projects are evaluated, how assets are valued, and how regulations and taxes affect returns, you&apos;ll be better equipped to make informed decisions.
                </p>
              </div>
              <div className="hero-image-wrapper-sv">
                <Image
                  src="/icons/Investment.png"
                  alt="Oil Tanker and Industrial Facility"
                  className="hero-image-sv"
                  fill
                  priority
                  style={{ objectFit: 'cover' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Explore Oil and Gas Investment Strategies Section */}
      <section className="strategies-section-sv">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h2 className="strategies-title-sv text-center">Explore Oil and Gas Investment Strategies</h2>
              <p className="strategies-subtitle-sv">
                Similar to any investment, success in energy investment depends on choosing the right approach for your goals. Understanding different oil and gas investment strategies helps you align your portfolio with what matters most to you, whether you&apos;re focused on growth, steady value, or sustainable impact.
              </p>
            </div>
          </div>
          <div className="row">
            {strategies.map((strategy, index) => (
              <div key={index} className="col-lg-4 col-md-6 mb-4">
                {strategy.isHeader ? (
                  <div className="strategy-header-card-sv">
                    <h3 className="strategy-header-title-sv">{strategy.title}</h3>
                    <Link href="/category/investment-strategies" className="strategy-header-button-sv" style={{ textDecoration: 'none' }}>
                      See All Investment Strategy Articles
                    </Link>
                  </div>
                ) : (
                  <div className="strategy-card-sv">
                    <h4 className="strategy-card-title-sv">{strategy.title}</h4>
                    <p className="strategy-card-description-sv">{strategy.description}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Valuing Oil and Gas Projects Section */}
      <section className="valuation-section-sv">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className='left-border-wrap'>
                <h2 className="valuation-title-sv">Valuing Oil and Gas Projects: Tools and Metrics</h2>
                <p className="valuation-subtitle-sv">
                  A sound investment starts with accurate valuation. Oil and gas projects are assessed using financial tools and industry metrics that reveal their potential worth. Valuation helps you decide whether a project is worth your capital.
                </p>
              </div>
            </div>
          </div>
          <div className="row align-items-start">
            <div className="col-lg-7">
              <div className="valuation-topics-list-sv">
                {valuationTopics.map((topic, index) => (
                  <div key={index} className="valuation-topic-item-sv">
                    <h3 className="valuation-topic-title-sv">{topic.title}</h3>
                    <p className="valuation-topic-description-sv">{topic.description}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-lg-5">
              <div className="valuation-image-wrapper-sv">
                <Image
                  src={heroImage}
                  alt="Financial Calculator and Charts"
                  className="valuation-image-sv"
                  fill
                  style={{ objectFit: 'cover' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who can Invest in Oil and Gas Section */}
      <section className="investor-section-sv">
        <div className="container">
          <div className="row">
            <div className="col-lg-5">
              <h2 className="investor-title-sv">Who can Invest in Oil and Gas?</h2>
              <p className="investor-description-sv">
                Because of the risks and regulatory requirements, some types of oil and gas investments are open only to specific investors. Knowing who qualifies ensures you understand your options and obligations.
              </p>
              <button className="investor-button-sv"
                  onClick={() => window.open('/uploads/Investor-Requirements.pdf', '_blank')}
              >
                Read The Investor Requirements Guide</button>
            </div>
            <div className="col-lg-7">
              <div className="investor-types-wrapper-sv">
                {investorTypes.map((type, index) => (
                  <div key={index} className="investor-type-item-sv">
                    <h3 className="investor-type-title-sv">{type.title}</h3>
                    <p className="investor-type-description-sv">{type.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tax Advantages and Cost Structures Section */}
      <section className="tax-section-sv">
        <div className="container">
          <div className="row align-items-center mb-5">
            <div className="col-lg-6">
              <h2 className="tax-title-sv">Tax Advantages and Cost Structures in Oil & Gas</h2>
            </div>
            <div className="col-lg-6">
              <p className="tax-subtitle-sv">
                Oil and gas investing comes with distinct tax advantages and cost considerations. Understanding them can significantly improve your after-tax returns.
              </p>
              <button className="tax-button-sv">Use The Investment Tax Estimator</button>
            </div>
          </div>
          <div className="row">
            {taxAdvantages.map((advantage, index) => (
              <div key={index} className="col-lg-3 col-md-6 mb-4">
                <div className="tax-advantage-card-sv">
                  <div className="tax-advantage-icon-sv">
                    <Image src={advantage.icon} alt='advantage.icon' />
                    </div>
                  <h4 className="tax-advantage-title-sv">{advantage.title}</h4>
                  <p className="tax-advantage-description-sv">{advantage.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Insights Section */}
      <section className="insights-section-sv">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h2 className="insights-title-sv">Featured Insights on Strategies & Valuation</h2>
            </div>
          </div>
          <div className="row">
            {featuredInsights.map((insight, index) => (
              <div key={index} className="col-lg-4 col-md-6 mb-4">
                <div className="insight-card-sv">
                  <div className="insight-image-wrapper-sv">
                    <Image
                      src={insight.image}
                      alt={insight.title}
                      className="insight-image-sv"
                      fill
                      style={{ objectFit: 'cover' }}
                    />
                  </div>
                  <div className="insight-content-sv">
                    <h4 className="insight-title-sv">
                      <Link href={insight.slug ? '/article/' + insight.slug : '#'} target={insight.slug ? '_blank' : undefined} style={{ textDecoration: 'none', color: 'inherit' }}>
                        {insight.title}
                      </Link>
                    </h4>
                    <p className="insight-description-sv">{insight.readmore_description}</p>
                    <Link href={insight.slug ? '/article/' + insight.slug : '#'} target={insight.slug ? '_blank' : undefined} className="insight-link-sv">
                      Read More
                      <Image src={roundarrow} alt='roundarrow' />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ready to Dive Deeper Section */}
      <section className="cta-section-sv">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-5 col-md-12">
              <h2 className="cta-title-sv">Ready to Dive Deeper Into Oil & Gas Investment?</h2>
            </div>
            <div className="col-lg-7 col-md-12">
              <p className="cta-description-sv">
                Access the resources you need to tailor your investment strategy, build your portfolio, and maximize returns on your energy investments.
              </p>
              <button className="cta-button-sv">Get Started</button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section-sv">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h2 className="faq-title-sv text-center">Frequently Asked Questions</h2>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <div className="faq-list-sv">
                {faqs.map((faq, index) => (
                  <div key={index} className="faq-item-sv">
                    <button
                      className={`faq-question-sv ${openFaq === index ? 'active-sv' : ''}`}
                      onClick={() => toggleFaq(index)}
                    >
                      <span>{faq.question}</span>
                      <span className="faq-icon-sv">
                        {openFaq === index ? '−' : '+'}
                      </span>
                    </button>
                    {openFaq === index && (
                      <div className="faq-answer-sv">
                        <p>{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

